/**
 * ECharts 얇은 래퍼 (DESIGN §9 — 차트는 Apache ECharts).
 *
 * `prefers-reduced-motion` 을 존중해 애니메이션을 끈다 (§8.6 접근성).
 * 차트는 보조 수단이고, 같은 값을 표로도 제공하는 것이 원칙이다 (ChartFrame).
 */

import type { EChartsOption } from 'echarts'
import { BarChart, CustomChart, LineChart, PieChart, ScatterChart } from 'echarts/charts'
import {
  DataZoomComponent,
  GraphicComponent,
  GridComponent,
  LegendComponent,
  MarkLineComponent,
  TitleComponent,
  TooltipComponent,
} from 'echarts/components'
import * as echarts from 'echarts/core'
import { CanvasRenderer } from 'echarts/renderers'
import { useEffect, useRef } from 'react'

echarts.use([
  BarChart,
  LineChart,
  ScatterChart,
  CustomChart,
  PieChart,
  GridComponent,
  LegendComponent,
  MarkLineComponent,
  TitleComponent,
  TooltipComponent,
  DataZoomComponent,
  GraphicComponent,
  CanvasRenderer,
])

interface Props {
  option: EChartsOption
  height?: number
  /** 스크린리더용 요약 — 차트만으로 정보가 전달되면 안 된다 */
  ariaLabel: string
}

export function EChart({ option, height = 240, ariaLabel }: Props) {
  const ref = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (!ref.current) return
    const reduced =
      typeof window !== 'undefined' &&
      window.matchMedia?.('(prefers-reduced-motion: reduce)').matches

    const chart = echarts.init(ref.current, undefined, { renderer: 'canvas' })
    chart.setOption({ animation: !reduced, ...option })

    const onResize = () => chart.resize()
    window.addEventListener('resize', onResize)
    const observer = new ResizeObserver(() => chart.resize())
    observer.observe(ref.current)

    return () => {
      window.removeEventListener('resize', onResize)
      observer.disconnect()
      chart.dispose()
    }
  }, [option])

  return (
    <div
      ref={ref}
      style={{ width: '100%', height }}
      role="img"
      aria-label={ariaLabel}
      data-testid="echart"
    />
  )
}
